import cx from 'classnames';
import {useCallback, useRef, useState} from 'react';
import {Popover} from 'react-tiny-popover';
import urls from 'urls';
import {EviteModal} from '~/modal';
import {Button} from '../atoms/Button';
import {Link} from '../atoms/Link';
import {Typography} from '../atoms/Typography';
import {pebble, tanzanite} from '../sass/base/_exports.sass';
import {AppleIcon} from '../svg/AppleIcon';
import {CalendarLineIcon} from '../svg/CalendarLineIcon';
import {GoogleIcon} from '../svg/GoogleIcon';
import {OutlookIcon} from '../svg/OutlookIcon';
import {isMobile} from '../utils/utils';

export const AddToCalendar = ({
  isButton,
  preview: isPreview,
  title = 'Add to calendar',
  text = '',
  ratio,
  className = '',
  googleCalUrl = window.calendar_urls?.google_cal_url ?? '',
  iCalUrl,
  outlookUrl,
  type = 'event',
  buttonSize = 'large',
  'data-type': dataType = 'calendar',
  'data-qa-id': dataQaId,
  'data-source': dataSource = '',
  'data-action': dataAction = 'download',
}) => {
  const ref = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const textToShow = text || title;
  const isMobileDevice = isMobile();

  const downloadCalendarEvent = useCallback(() => {
    window.location.href = `${window.location.origin}/event/${window.event_id}/export/ical/?gid=${window.guest_id}`;
  }, []);

  // biome-ignore-start lint/style/useNamingConvention: expected parameter names
  const createAddToCalendarModal = useCallback(
    () =>
      new EviteModal({
        width: '640px',
        url: urls.get('modal_add_to_calendar', {
          event_id: window.event_id,
        }),
        url_parameters: {
          guest_id: window.guest_id,
          is_mobile: isMobileDevice,
        },
      }),
    [isMobileDevice]
  );
  // biome-ignore-end lint/style/useNamingConvention: expected parameter names

  const handleSelectAppleOrOutlookCalendar = useCallback(
    (event) => {
      event.preventDefault();

      if (isMobileDevice) {
        downloadCalendarEvent();
      } else {
        const addToCalendarModal = createAddToCalendarModal();
        addToCalendarModal.show();
      }

      return false;
    },
    [createAddToCalendarModal, downloadCalendarEvent, isMobileDevice]
  );

  const handleClick = (event) => {
    event.preventDefault();
    setMenuOpen((prevState) => !prevState);
  };

  const renderGoogleCalendarButton = useCallback(
    () =>
      googleCalUrl ? (
        <Link
          as="transparent"
          variant="small"
          className="add-to-calendar__calendar-option"
          href={googleCalUrl}
          target="_blank"
          data-qa-id="add-to-cal__google"
        >
          <GoogleIcon ratio={0.75} />
          Add to Google calendar
        </Link>
      ) : null,
    [googleCalUrl]
  );

  const renderAppleCalendarButton = useCallback(() => {
    if (iCalUrl) {
      return (
        <Link
          as="transparent"
          variant="small"
          className="add-to-calendar__calendar-option"
          href={iCalUrl}
          target="_blank"
          data-qa-id="add-to-cal__apple"
        >
          <AppleIcon ratio={0.75} />
          Add to Apple calendar
        </Link>
      );
    }
    if (type === 'event') {
      return (
        <Button
          variant="transparent"
          className="add-to-calendar__calendar-option"
          onClick={handleSelectAppleOrOutlookCalendar}
          data-qa-id="add-to-cal__apple"
        >
          <AppleIcon ratio={0.75} />
          Add to Apple calendar
        </Button>
      );
    }
    return null;
  }, [iCalUrl, type, handleSelectAppleOrOutlookCalendar]);

  const renderOutlookCalendarButton = useCallback(() => {
    if (outlookUrl) {
      return (
        <Link
          as="transparent"
          variant="small"
          className="add-to-calendar__calendar-option"
          href={outlookUrl}
          target="_blank"
          data-qa-id="add-to-cal__outlook"
        >
          <OutlookIcon ratio={0.75} />
          Add to Outlook calendar
        </Link>
      );
    }
    if (type === 'event') {
      return (
        <Button
          variant="transparent"
          className="add-to-calendar__calendar-option"
          onClick={handleSelectAppleOrOutlookCalendar}
          data-qa-id="add-to-cal__outlook"
        >
          <OutlookIcon ratio={0.75} />
          Add to Outlook calendar
        </Button>
      );
    }
    return null;
  }, [outlookUrl, type, handleSelectAppleOrOutlookCalendar]);

  const calendarLinksAvailable = type === 'event' || googleCalUrl || iCalUrl || outlookUrl;

  // Not clickable if its preview mode.
  return calendarLinksAvailable ? (
    <Popover
      isOpen={menuOpen}
      positions={['bottom', 'top']}
      padding={8}
      onClickOutside={handleClick}
      content={
        <div id="calendar-options-menu" className="add-to-calendar__calendar-options-menu">
          {renderGoogleCalendarButton()}
          {renderAppleCalendarButton()}
          {renderOutlookCalendarButton()}
        </div>
      }
    >
      <button
        id="add-to-calendar"
        data-qa-id={dataQaId ?? `add-to-calendar`}
        className={cx('unstyled-button', 'link', buttonSize, className, 'add-to-calendar-btn', {
          disabled: isPreview,
        })}
        title={title}
        data-type={dataType}
        data-source={dataSource}
        data-action={dataAction}
        onClick={handleClick}
        disabled={isPreview}
        ref={ref}
        type="button"
      >
        {isButton ? (
          `Add to calendar`
        ) : (
          <>
            <CalendarLineIcon ratio={ratio} color={isPreview ? pebble : tanzanite} />
            <Typography
              className="add-to-calendar-btn-text"
              color={isPreview ? 'pebble' : 'tanzanite'}
              variant="paragraph2"
            >
              {textToShow}
            </Typography>
          </>
        )}
      </button>
    </Popover>
  ) : null;
};
